import { Action, ButtonGroup, Card } from 'corso-admin-ui';
import { Pegasus, toEnum } from 'corso-types';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import GridList from '~/components/GridList';
import { automationTemplatesWithIds } from '~/features/settings/automations/AutomationTemplates';
import AutomationRuleList from '~/features/settings/automations/AutomationsList';
import { useAutomationsForEventType } from '~/features/settings/hooks/useAutomations';
import { useStoreId } from '~/providers/StoreUserProvider';
import { orderEditActionLabel } from '~/utils/enumNameMaps';

// One template per Pegasus.OrderEditType lives in AutomationTemplates; pull
// just the orderEditing ones so this card and the create flow stay in sync.
const orderEditingTemplates = automationTemplatesWithIds.filter(
  (t) => t.automation.rule.event.type === Pegasus.EventType.orderEditing,
);

// ----- rule hooks (grouped by editType, not eventType) -----

const orderEditTypes = Object.values(Pegasus.OrderEditType);

export function useOrderEditingRules() {
  const automations = useAutomationsForEventType(
    Pegasus.EventType.orderEditing,
  );
  return useMemo(
    () =>
      new Map(
        orderEditTypes.map((editType) => [
          editType,
          automations.filter((a) => {
            if (a.rule.event.type !== Pegasus.EventType.orderEditing) {
              return false;
            }
            return (
              toEnum(a.rule.event.params.editType, Pegasus.OrderEditType) ===
              editType
            );
          }),
        ]),
      ),
    [automations],
  );
}

export function useOrderEditingRuleCount() {
  const orderEditingRules = useOrderEditingRules();
  return [...orderEditingRules.values()].reduce(
    (count, rules) => count + rules.length,
    0,
  );
}

// ----- UI -----

function OrderEditingAddRule() {
  const storeId = useStoreId();
  const location = useLocation();

  return (
    <GridList columns={2} data={orderEditingTemplates}>
      {({ id, icon, instructions, automation: { name } }) => (
        <GridList.ItemLink
          key={id}
          to={{
            pathname: `/${storeId}/settings/automations/create`,
            search: new URLSearchParams({
              template: id,
              returnTo: location.pathname,
            }).toString(),
          }}
          label={name}
          description={instructions}
          icon={icon}
          variant="primary"
        />
      )}
    </GridList>
  );
}

export default function OrderEditingRules() {
  const orderEditingRules = useOrderEditingRules();
  const hasOrderEditingRules = !!useOrderEditingRuleCount();

  // Reactively default to 'list' once rules are loaded, but freeze the choice
  // as soon as the user toggles either button.
  const [userView, setView] = useState<'list' | 'create' | null>(null);
  const view = userView ?? (hasOrderEditingRules ? 'list' : 'create');

  return (
    <Card>
      <Card.Line as="header">
        <Card.Heading>Automations</Card.Heading>
        <ButtonGroup horizontal>
          <Action
            onClick={() => setView('list')}
            pressed={view === 'list'}
            icon="rule"
            accessibilityLabel="View existing automations"
            disabled={!hasOrderEditingRules}
          >
            View
          </Action>
          <Action
            onClick={() => setView('create')}
            pressed={view === 'create'}
            icon="add"
            accessibilityLabel="Create a new automation"
          >
            Create
          </Action>
        </ButtonGroup>
      </Card.Line>

      {view === 'create' || !hasOrderEditingRules ? (
        <OrderEditingAddRule />
      ) : (
        [...orderEditingRules.entries()].map(([editType, automations]) => {
          if (!automations.length) return null;

          return (
            <AutomationRuleList
              key={editType}
              automations={automations}
              label={orderEditActionLabel[editType]}
            />
          );
        })
      )}
    </Card>
  );
}
